import React, { useEffect, useState } from 'react';
import { Box, Button, Card, Container, Grid, Stack, SwipeableDrawer, Switch, TextField, Typography } from '@mui/material';
import { dispatch, useSelector } from '../../../redux/store';
import { PATH_DASHBOARD } from '../../../routes/paths';
import useSettings from '../../../hooks/useSettings';
import Page from '../../../components/Page';
import Iconify from '../../../components/Iconify';
import HeaderBreadcrumbs from '../../../components/HeaderBreadcrumbs';
import DataGridTable from '../../../components/table/DataGridTable';
import { LoadingButton } from '@mui/lab';
import { toast } from 'react-toastify';
import { accessVerify, capitalize, safeJSONParse } from '../../../utils/common';
import { addPaymentTypes, getPaymentTypes, updatePaymentTypes } from '../../../redux/slices/financeRedux/paymentTypeRedux';
import PermissionRequired from '../../errorPages/permissionRequired';

export default function PaymentTypes() {
  const dataModel = {
    id: '',
    name: '',
    description: '',
    isCashFlowOption: false,
    isCashFlowTransferOption: false,
    isAddToCashflow: false,
  };
  const [selectedDataObj, setSelectedDataObj] = useState(dataModel);
  const { themeStretch } = useSettings();
  const [isAdd, setIsAdd] = useState(false);
  const [isModelOpen, setIsModelOpen] = useState(false);
  const [paymentTypesList, setPaymentTypesList] = useState([]);

  const { data, addData } = useSelector((state) => state.paymentTypes);

  useEffect(() => {
    dispatch(getPaymentTypes());
  }, []);

  useEffect(() => {
    setPaymentTypesList(
      data?.map((value) => {
        return {
          ...value,
          locationData: safeJSONParse(value?.locationData),
          userData: safeJSONParse(value?.userData),
        };
      })
    );
    setIsModelOpen(false);
  }, [data]);

  const manageModel = (modelData, type) => {
    if (isModelOpen) {
      setIsModelOpen(false);
    } else {
      setIsModelOpen(true);
      setIsAdd(type === 'add');
      if (type === 'add') {
        setSelectedDataObj(dataModel);
      } else {
        setSelectedDataObj(modelData);
      }
    }
  };

  useEffect(() => {
    if (addData?.data) {
      dispatch(getPaymentTypes());
    }
  }, [addData]);

  const updateEditingData = (key, value) => {
    setSelectedDataObj({
      ...selectedDataObj,
      [key]: value,
    });
  };

  const columns = !paymentTypesList?.[0]
    ? []
    : Object.keys(paymentTypesList?.[0])
        .map((value) => {
          if (['isVerifier', 'isIncrement', 'locationData', 'userData'].includes(value)) {
            return {};
          } else if (value === 'isCashFlowOption') {
            return {
              accessorKey: 'isCashFlowOption',
              header: 'Show In Cashflow',
              Cell: ({ cell, row }) => {
                return row?.original?.isCashFlowOption === 1 ? 'Yes' : 'No';
              },
            };
          } else if (value === 'isCashFlowTransferOption') {
            return {
              accessorKey: 'isCashFlowTransferOption',
              header: 'Show In Cashflow Transfer',
              Cell: ({ cell, row }) => {
                return row?.original?.isCashFlowTransferOption === 1 ? 'Yes' : 'No';
              },
            };
          } else if (value === 'isAddToCashflow') {
            return {
              accessorKey: 'isAddToCashflow',
              header: 'Add to Cashflow when closing the POS drawer',
              Cell: ({ cell, row }) => {
                return row?.original?.isAddToCashflow === 1 ? 'Yes' : 'No';
              },
            };
          } else {
            return {
              accessorKey: value,
              header: capitalize(value),
            };
          }
        })
        ?.filter((value) => value?.accessorKey);

  const [rowSelection, setRowSelection] = useState(false);

  const handleSubmit = () => {
    if (selectedDataObj.name.length === 0) {
      toast.error('Asset name is required!');
    } else if (selectedDataObj.description.length === 0) {
      toast.error('Asset description is required!');
    } else {
      dispatch(isAdd ? addPaymentTypes(selectedDataObj) : updatePaymentTypes(selectedDataObj));
    }
  };
  return (
    <Page title={`Transaction Medium`}>
      <Container maxWidth={themeStretch ? false : 'lg'}>
        <HeaderBreadcrumbs
          heading={`Transaction Medium`}
          links={[
            { name: 'Dashboard', href: PATH_DASHBOARD.root },
            {
              name: 'Finance',
              href: '',
            },
            { name: `Transaction Medium` },
          ]}
          action={
            <Button
              variant='contained'
              startIcon={<Iconify icon='eva:plus-fill' />}
              onClick={() => {
                manageModel(dataModel, 'add');
              }}
            >
              New Payment Type
            </Button>
          }
        />

        {accessVerify('PAYMENT_TYPES_VIEW') ? (
          <DataGridTable
            name={'Payment Types'}
            data={paymentTypesList}
            column={columns}
            isLoading={false}
            onRowClick={(row) => {
              manageModel(row?.original, 'edit');
            }}
            isRowClickable={true}
            rowSelection={rowSelection}
            setRowSelection={setRowSelection}
            enableRowSelection={false}
            enableRowActions={false}
            renderRowActionItems={(value, closeMenu) => []}
          />
        ) : (
          <PermissionRequired />
        )}

        <SwipeableDrawer
          anchor={'top'}
          open={isModelOpen}
          onOpen={() => setIsModelOpen(true)}
          onClose={() => setIsModelOpen(false)}
          PaperProps={{
            sx: {
              width: '90%',
              height: '90%',
              marginLeft: '5%',
              marginTop: '3%',
              borderRadius: '10px',
            },
          }}
        >
          <Box sx={{ p: 2 }}>
            <h1>{isAdd ? 'Add' : 'Edit'} Payment Type</h1>
          </Box>
          <Grid container spacing={1}>
            <Grid item xs={12} md={8}>
              <Stack spacing={3} sx={{ p: 3 }}>
                <Card sx={{ p: 3 }}>
                  <Typography variant='subtitle1' padding='10px 0'>
                    Asset Information
                  </Typography>
                  <Stack spacing={3}>
                    <TextField fullWidth label='Name' value={selectedDataObj?.name} onChange={(e) => updateEditingData('name', e.target.value)} />
                    <TextField fullWidth label='Description' value={selectedDataObj?.description} onChange={(e) => updateEditingData('description', e.target.value)} />

                    <Stack direction='row' alignItems='center'>
                      <Typography variant='overline' sx={{ mr: 1.5 }}>
                        Show in Cashflow Options
                      </Typography>
                      <Switch
                        checked={selectedDataObj?.isCashFlowOption}
                        onChange={(e) => {
                          updateEditingData('isCashFlowOption', e.target.checked);
                        }}
                      />
                      <Typography variant='overline' sx={{ ml: 1.5 }}>
                        Don't show in Cashflow Options
                      </Typography>
                    </Stack>
                    <Stack direction='row' alignItems='center'>
                      <Typography variant='overline' sx={{ mr: 1.5 }}>
                        Show in Cashflown Transfer Options
                      </Typography>
                      <Switch
                        checked={selectedDataObj?.isCashFlowTransferOption}
                        onChange={(e) => {
                          updateEditingData('isCashFlowTransferOption', e.target.checked);
                        }}
                      />
                      <Typography variant='overline' sx={{ ml: 1.5 }}>
                        Don't show in Cashflow Transfer Options
                      </Typography>
                    </Stack>
                    <Stack direction='row' alignItems='center'>
                      <Typography variant='overline' sx={{ mr: 1.5 }}>
                        Don't Add to Cashflow when closing the POS drawer
                      </Typography>
                      <Switch
                        checked={selectedDataObj?.isAddToCashflow}
                        onChange={(e) => {
                          updateEditingData('isAddToCashflow', e.target.checked);
                        }}
                      />
                      <Typography variant='overline' sx={{ ml: 1.5 }}>
                        Add to Cashflow when closing the POS drawer
                      </Typography>
                    </Stack>
                  </Stack>
                </Card>
              </Stack>
            </Grid>

            <Grid item xs={12} md={4}>
              <Card sx={{ p: 3 }}>
                <Stack spacing={3} sx={{ p: 3 }}>
                  <LoadingButton type='submit' variant='contained' size='large' style={{ width: '100%' }} onClick={handleSubmit}>
                    {isAdd ? 'Add Payment Type' : 'Save Changes'}
                  </LoadingButton>
                  <Button
                    color='info'
                    variant='outlined'
                    size='large'
                    style={{ width: '100%' }}
                    onClick={() => {
                      manageModel(dataModel, 'add');
                    }}
                  >
                    Close
                  </Button>
                </Stack>
              </Card>
            </Grid>
          </Grid>
        </SwipeableDrawer>
      </Container>
    </Page>
  );
}
